import './player.css';
import { videoDe, hayVideo, posterDe } from '../../config/videos.js';

// ---------------------------------------------------------------------------
// REPRODUCTOR PROPIO.
//
// Por qué no el de YouTube: el embed pone su marca sobre nuestro diseño, mete
// recomendaciones al terminar —de la competencia, con frecuencia— y se lleva al
// visitante fuera del sitio. Para una plataforma de pago, el reproductor es
// parte del producto, no un widget prestado.
//
// CÓMO FUNCIONA
//
//   · FACHADA. El <video> no existe hasta el primer clic. Antes solo hay la
//     portada, que es una imagen. Así una página con muestra pesa lo mismo que
//     una sin ella —el mismo criterio que llevó el 3D del home a carga
//     diferida—.
//   · FORMATOS. `.mp4` y `.webm` los toca el navegador solo. `.m3u8` (HLS, que
//     es lo que entrega Cloudflare Stream y cualquier CDN de video) va nativo
//     en Safari e iOS; en Chrome y Firefox necesita hls.js, que se importa en
//     diferido Y dentro de un try: si la dependencia no está instalada, el
//     reproductor lo dice en vez de quedarse en negro.
//   · YOUTUBE. Sigue soportado como puente para las piezas que hoy solo viven
//     ahí. En cuanto la misma clave gane `src`, el embed deja de usarse sin
//     tocar ninguna página.
//
// Controles propios, no los del navegador: los nativos cambian de forma en cada
// sistema y rompen el DS.
// ---------------------------------------------------------------------------

const IC = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>',
  vol: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 5V4L8 9H4z"/><path d="M16.5 12a3.5 3.5 0 0 0-2-3.16v6.32A3.5 3.5 0 0 0 16.5 12z"/><path d="M14.5 3.23v2.06A6.99 6.99 0 0 1 19 12a6.99 6.99 0 0 1-4.5 6.71v2.06A9 9 0 0 0 21 12a9 9 0 0 0-6.5-8.77z"/></svg>',
  mute: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 5V4L8 9H4z"/><path d="m16.2 9.4 1.4-1.4 2.1 2.1 2.1-2.1 1.4 1.4-2.1 2.1 2.1 2.1-1.4 1.4-2.1-2.1-2.1 2.1-1.4-1.4 2.1-2.1z"/></svg>',
  full: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5v2H6v3H4zm11-5h5v5h-2V6h-3V4zM4 15h2v3h3v2H4v-5zm14 0h2v5h-5v-2h3v-3z"/></svg>',
  exit: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4V7h3V4h2zm6 0h2v3h3v2h-5V4zM4 15h5v5H7v-3H4v-2zm11 0h5v2h-3v3h-2v-5z"/></svg>',
};

/** mm:ss, o h:mm:ss cuando pasa de la hora. */
function reloj(s) {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const t = Math.floor(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const g = t % 60;
  const dd = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${dd(m)}:${dd(g)}` : `${m}:${dd(g)}`;
}

const esHls = (src) => /\.m3u8(\?|$)/i.test(src);

/**
 * Conecta la fuente al <video>. Devuelve un mensaje de error, o null si bien.
 * HLS sin soporte nativo necesita hls.js; si no está instalado se avisa en vez
 * de dejar un rectángulo negro sin explicación.
 */
async function conectar(el, src) {
  if (!esHls(src) || el.canPlayType('application/vnd.apple.mpegurl')) {
    el.src = src;
    return null;
  }
  try {
    const { default: Hls } = await import('hls.js');
    if (!Hls.isSupported()) return 'Este navegador no puede reproducir el video.';
    const hls = new Hls({ capLevelToPlayerSize: true });
    hls.loadSource(src);
    hls.attachMedia(el);
    el.addEventListener('zk-destroy', () => hls.destroy(), { once: true });
    return null;
  } catch {
    return 'Falta instalar hls.js para reproducir este formato.';
  }
}

/** El embed de YouTube, mientras una pieza no tenga archivo propio. */
function embedYouTube(destino, video) {
  const f = document.createElement('iframe');
  f.src = `https://www.youtube-nocookie.com/embed/${video.yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  f.title = video.titulo;
  f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  f.allowFullscreen = true;
  destino.appendChild(f);
  return f;
}

function aviso(destino, texto) {
  const p = document.createElement('p');
  p.className = 'zk-pl__aviso';
  p.textContent = texto;
  destino.appendChild(p);
}

/** El reproductor propio, con sus controles. */
function reproductorPropio(destino, video) {
  const caja = document.createElement('div');
  caja.className = 'zk-pl';
  caja.innerHTML = `
    <video class="zk-pl__v" playsinline preload="metadata" poster="${video.poster}"></video>
    <button type="button" class="zk-pl__big" aria-label="Reproducir">${IC.play}</button>
    <div class="zk-pl__bar">
      <button type="button" class="zk-pl__b" data-a="play" aria-label="Reproducir">${IC.play}</button>
      <div class="zk-pl__seek">
        <div class="zk-pl__buf"></div>
        <div class="zk-pl__prog"></div>
        <input class="zk-pl__range" type="range" min="0" max="1000" value="0" step="1" aria-label="Avance del video" />
      </div>
      <span class="zk-pl__t"><b>0:00</b> / <span class="zk-pl__dur">--:--</span></span>
      <button type="button" class="zk-pl__b" data-a="mute" aria-label="Silenciar">${IC.vol}</button>
      <button type="button" class="zk-pl__b" data-a="full" aria-label="Pantalla completa">${IC.full}</button>
    </div>`;
  destino.appendChild(caja);

  const el = caja.querySelector('.zk-pl__v');
  const big = caja.querySelector('.zk-pl__big');
  const bPlay = caja.querySelector('[data-a="play"]');
  const bMute = caja.querySelector('[data-a="mute"]');
  const bFull = caja.querySelector('[data-a="full"]');
  const range = caja.querySelector('.zk-pl__range');
  const prog = caja.querySelector('.zk-pl__prog');
  const buf = caja.querySelector('.zk-pl__buf');
  const cur = caja.querySelector('.zk-pl__t b');
  const dur = caja.querySelector('.zk-pl__dur');

  const alternar = () => (el.paused ? el.play() : el.pause());

  const pintarPlay = () => {
    const icono = el.paused ? IC.play : IC.pause;
    const etiqueta = el.paused ? 'Reproducir' : 'Pausar';
    bPlay.innerHTML = icono;
    bPlay.setAttribute('aria-label', etiqueta);
    big.innerHTML = icono;
    big.setAttribute('aria-label', etiqueta);
    caja.classList.toggle('is-playing', !el.paused);
  };

  // El usuario arrastrando manda: mientras lo hace, el tiempo del video no
  // mueve la barra o pelearían entre sí.
  let arrastrando = false;
  const pintarTiempo = () => {
    cur.textContent = reloj(el.currentTime);
    if (!el.duration) return;
    const p = (el.currentTime / el.duration) * 100;
    prog.style.width = `${p}%`;
    if (!arrastrando) range.value = String(Math.round(p * 10));
  };

  el.addEventListener('loadedmetadata', () => { dur.textContent = reloj(el.duration); pintarTiempo(); });
  el.addEventListener('timeupdate', pintarTiempo);
  el.addEventListener('play', pintarPlay);
  el.addEventListener('pause', pintarPlay);
  el.addEventListener('ended', () => { caja.classList.remove('is-playing'); pintarPlay(); });
  el.addEventListener('progress', () => {
    if (!el.buffered.length || !el.duration) return;
    buf.style.width = `${(el.buffered.end(el.buffered.length - 1) / el.duration) * 100}%`;
  });
  el.addEventListener('error', () => aviso(caja, 'No se pudo cargar el video.'));

  big.addEventListener('click', alternar);
  bPlay.addEventListener('click', alternar);
  el.addEventListener('click', alternar);

  bMute.addEventListener('click', () => {
    el.muted = !el.muted;
    bMute.innerHTML = el.muted ? IC.mute : IC.vol;
    bMute.setAttribute('aria-label', el.muted ? 'Activar sonido' : 'Silenciar');
  });

  bFull.addEventListener('click', () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (caja.requestFullscreen) caja.requestFullscreen();
    else el.webkitEnterFullscreen?.();      // iPhone: solo el <video> va a pantalla completa
  });
  document.addEventListener('fullscreenchange', () => {
    const on = document.fullscreenElement === caja;
    bFull.innerHTML = on ? IC.exit : IC.full;
    bFull.setAttribute('aria-label', on ? 'Salir de pantalla completa' : 'Pantalla completa');
  });

  const buscar = () => { if (el.duration) el.currentTime = (Number(range.value) / 1000) * el.duration; };
  range.addEventListener('input', () => { arrastrando = true; prog.style.width = `${Number(range.value) / 10}%`; });
  range.addEventListener('change', () => { buscar(); arrastrando = false; });

  // Teclado sobre el reproductor. Sin esto, un video a pantalla completa se
  // queda sin forma de pausarse salvo con el mouse.
  caja.tabIndex = 0;
  caja.addEventListener('keydown', (e) => {
    const t = { ' ': 'play', k: 'play', ArrowLeft: -5, ArrowRight: 5, j: -10, l: 10 }[e.key];
    if (t === 'play') { e.preventDefault(); alternar(); return; }
    if (typeof t === 'number') { e.preventDefault(); el.currentTime = Math.max(0, el.currentTime + t); return; }
    if (e.key === 'm') { e.preventDefault(); bMute.click(); return; }
    if (e.key === 'f') { e.preventDefault(); bFull.click(); }
  });

  // Los controles se van solos mientras se reproduce, y vuelven al menor
  // movimiento o al tocar la pantalla.
  let ocioso;
  const despertar = () => {
    caja.classList.remove('is-idle');
    clearTimeout(ocioso);
    ocioso = setTimeout(() => { if (!el.paused) caja.classList.add('is-idle'); }, 2600);
  };
  ['pointermove', 'pointerdown', 'focusin'].forEach((ev) => caja.addEventListener(ev, despertar));
  caja.addEventListener('pointerleave', () => { if (!el.paused) caja.classList.add('is-idle'); });

  conectar(el, video.src).then((err) => {
    if (err) { aviso(caja, err); return; }
    el.play().catch(() => {});     // si el navegador bloquea el autoplay, queda el botón grande
    despertar();
  });

  return caja;
}

/**
 * Reproduce la pieza `clave` dentro de `destino`.
 * Devuelve false —sin tocar el DOM— cuando no hay nada que reproducir, para que
 * quien llama pueda mostrar otra cosa en vez de un marco vacío.
 */
export function reproducir(destino, clave) {
  if (!destino) return false;
  // Ya montado. Se pregunta por el reproductor, NO por "¿tiene hijos?": el
  // marco del hero lleva dentro su portada, su scrim y su botón de play, así
  // que contar hijos lo daba por montado y nunca arrancaba.
  if (destino.querySelector('.zk-pl, iframe')) return true;
  const video = videoDe(clave);
  if (!video || (!video.src && !video.yt)) return false;
  if (video.src) reproductorPropio(destino, video);
  else embedYouTube(destino, video);
  return true;
}

/**
 * Corta la reproducción y quita el reproductor (para cerrar un lightbox).
 *
 * Quita SOLO el reproductor, no vacía el contenedor: el marco del hero lleva
 * dentro su portada y su botón de play, y vaciarlo dejaba el hueco en negro al
 * reabrir el modal.
 */
export function detener(destino) {
  if (!destino) return;
  destino.querySelectorAll('.zk-pl, iframe').forEach((el) => {
    el.querySelector?.('video')?.dispatchEvent(new Event('zk-destroy'));
    if (el.tagName === 'VIDEO') el.dispatchEvent(new Event('zk-destroy'));
    el.remove();
  });
}

// ---------------------------------------------------------------------------
// LA FACHADA de las páginas de curso y de certificación.
//
// Las dos plantillas dibujaban el mismo marco con el mismo botón de play, y las
// dos abrían un contenedor vacío. Ahora el marco lo arma esto, en un solo
// lugar, y con una regla: SI NO HAY PIEZA, NO HAY BOTÓN. En vez de un play que
// no reproduce, el marco sale con su portada y dice que la muestra está en
// preparación —que es la verdad, y se nota a la primera—.
// ---------------------------------------------------------------------------

/** El marco de la muestra, listo para `conectarTrailer`. */
export function trailer({ clave, clase, etiqueta, titulo, flecha }) {
  const hay = hayVideo(clave);
  const abre = hay ? 'button type="button" class="fr" aria-label="' + etiqueta + '"' : 'div class="fr is-soon"';
  return `
          <div class="${clase} zk-tr" data-open="false" data-video="${clave}">
            <${abre}>
              <img class="po" src="${posterDe(clave)}" alt="" />
              <span class="md"></span>
              <span class="vg" aria-hidden="true"></span>
              ${hay ? `<span class="pl" aria-hidden="true">${flecha}</span>` : ''}
              <span class="lb"><b>${hay ? titulo : 'Muestra en preparación'}</b></span>
            </${hay ? 'button' : 'div'}>
          </div>`;
}

/**
 * Para los marcos con animación de apertura propia —el hero de Especialízate y
 * "¿Qué es Zenkai?"—: cuando no hay pieza, el marco SIGUE ABRIÉNDOSE y dentro
 * aparece este aviso.
 *
 * Antes aquí se deshabilitaba el botón. Fue un error: esos dos marcos tienen una
 * animación diseñada —la banda pasa de 21:9 a 16:9 y la página se recentra— que
 * funcionaba perfecto aunque no hubiera video, y apagar el botón se la llevó
 * puesta. Ser honesto sobre que falta el video no vale quitarle al visitante
 * algo que ya servía: se puede tener las dos cosas.
 */
export function proximamente(destino) {
  if (!destino || destino.querySelector('.zk-pl__pronto')) return;
  const p = document.createElement('p');
  p.className = 'zk-pl__pronto';
  p.setAttribute('role', 'status');
  p.textContent = 'Este video está en producción. Muy pronto.';
  destino.appendChild(p);
}

/** Le da vida a los marcos que `trailer()` dejó en la página. */
export function conectarTrailer(raiz) {
  raiz.querySelectorAll('.zk-tr').forEach((tr) => {
    const marco = tr.querySelector('.fr');
    // La portada puede no existir todavía (quees-poster.jpg, por ejemplo): que
    // el marco caiga a su degradado en vez de enseñar el icono de imagen rota.
    tr.querySelector('.po').addEventListener('error', () => marco.classList.add('no-poster'));
    if (marco.tagName !== 'BUTTON') return;
    marco.addEventListener('click', () => {
      if (reproducir(tr.querySelector('.md'), tr.dataset.video)) tr.dataset.open = 'true';
    });
  });
}
